import Footer from "@/components/footer";
import Header from "@/components/header";
import CounsellingCTA from "@/components/CounsellingCTA";
import "@/styles/index.css";
import "@/public/assets/css/screen.css";
import "@/public/assets/css/index.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../ug_webinar/OurSpeaker.css";
import "../../seminar/styles/seminar-animations.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "swiper/css";
import "swiper/css/pagination";
import App from "next/app";
import Cookies from "js-cookie";
import { CartProvider } from "../context/CartContext.js";
import SeminarRegistration from "../components/SeminarRegistration";
import CreativeSeminarBanner from "../components/CreativeSeminarBanner";
import { useRouter } from "next/router";
import React from "react";

const SEMINAR_TARGET_DATE = "2026-06-28T11:00:00+05:30";
const SEMINAR_ID = "neet-ug-2026-summit";

function parseSeminarDateTime(date, time) {
  if (!date || !time) {
    return new Date(SEMINAR_TARGET_DATE).getTime();
  }

  const cleanTime = String(time)
    .replace(/\s*IST\s*$/i, "")
    .trim();

  const parsedDate = new Date(`${date} ${cleanTime} GMT+0530`);

  if (Number.isNaN(parsedDate.getTime())) {
    return new Date(SEMINAR_TARGET_DATE).getTime();
  }

  return parsedDate.getTime();
}

function getSeminarConfigUrl() {
  const baseUrl = process.env.NEXT_PUBLIC_ADMIN_URL || "";

  return `${baseUrl.replace(/\/+$/, "")}/api/seminar-config/${SEMINAR_ID}`;
}

function SeminarRegistrationModalManager() {
  const [showPosterModal, setShowPosterModal] = React.useState(false);
  const [showSeminarModal, setShowSeminarModal] = React.useState(false);
  const [targetDate, setTargetDate] = React.useState(
    new Date(SEMINAR_TARGET_DATE).getTime()
  );
  const [configLoaded, setConfigLoaded] = React.useState(false);

  const timerRef = React.useRef(null);
  const expiryIntervalRef = React.useRef(null);
  const router = useRouter();

  const blockedRoutes = ["/ugwebinar", "/checkout"];

  const isSeminarPage = router.pathname === "/seminar";

  const isBlockedRoute = blockedRoutes.some((route) =>
    router.pathname.startsWith(route)
  );

  const isEventExpired = React.useCallback(() => {
    return Date.now() >= targetDate;
  }, [targetDate]);

  const closeAllModals = React.useCallback(() => {
    setShowPosterModal(false);
    setShowSeminarModal(false);
  }, []);

  const handleCloseAll = React.useCallback(() => {
    closeAllModals();

    if (typeof document !== "undefined") {
      document.body.style.overflow = "auto";
    }
  }, [closeAllModals]);

  React.useEffect(() => {
    let isMounted = true;

    async function fetchSeminarConfig() {
      try {
        const apiUrl = getSeminarConfigUrl();

        const res = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          cache: "no-store",
        });

        const json = await res.json();

        if (!res.ok) {
          console.error("Seminar config API error:", res.status, json);
          return;
        }

        const cfg = json?.data || json;

        if (!isMounted) return;

        if (cfg?.date && cfg?.time) {
          const dynamicTargetDate = parseSeminarDateTime(cfg.date, cfg.time);
          setTargetDate(dynamicTargetDate);
        }
      } catch (error) {
        console.error("Seminar config fetch error:", error);
      } finally {
        if (isMounted) {
          setConfigLoaded(true);
        }
      }
    }

    fetchSeminarConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const startModalTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    closeAllModals();

    if (isBlockedRoute) {
      document.body.style.overflow = "auto";
      return;
    }

    if (isEventExpired()) {
      document.body.style.overflow = "auto";
      return;
    }

    if (typeof window !== "undefined") {
      const isRegistered = localStorage.getItem("seminarRegistered");

      if (isRegistered === "true") {
        document.body.style.overflow = "auto";
        return;
      }
    }

    timerRef.current = setTimeout(() => {
      if (isEventExpired()) {
        handleCloseAll();
        return;
      }

      if (isSeminarPage) {
        setShowSeminarModal(true);
      } else {
        setShowPosterModal(true);
      }
    }, 5000);
  }, [
    isBlockedRoute,
    isSeminarPage,
    closeAllModals,
    isEventExpired,
    handleCloseAll,
  ]);

  React.useEffect(() => {
    if (!configLoaded) return;

    startModalTimer();

    router.events.on("routeChangeComplete", startModalTimer);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      router.events.off("routeChangeComplete", startModalTimer);
    };
  }, [router.events, startModalTimer, configLoaded]);

  React.useEffect(() => {
    if (!configLoaded) return;

    if (expiryIntervalRef.current) {
      clearInterval(expiryIntervalRef.current);
    }

    expiryIntervalRef.current = setInterval(() => {
      if (isEventExpired()) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        handleCloseAll();
      }
    }, 1000);

    return () => {
      if (expiryIntervalRef.current) {
        clearInterval(expiryIntervalRef.current);
      }
    };
  }, [isEventExpired, handleCloseAll, configLoaded]);

  React.useEffect(() => {
    if (isBlockedRoute || isEventExpired()) {
      document.body.style.overflow = "auto";
      return;
    }

    document.body.style.overflow =
      showPosterModal || showSeminarModal ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showPosterModal, showSeminarModal, isBlockedRoute, isEventExpired]);

  const handlePosterClick = () => {
    setShowPosterModal(false);
    document.body.style.overflow = "auto";

    if (isEventExpired()) {
      handleCloseAll();
      return;
    }

    if (router.pathname !== "/seminar") {
      router.push("/seminar");
      return;
    }

    setShowSeminarModal(true);
  };

  if (!configLoaded || isEventExpired()) {
    return null;
  }

  return (
    <>
      {showPosterModal && !isBlockedRoute && !isEventExpired() && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
          onClick={handleCloseAll}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <CreativeSeminarBanner
              onClose={handleCloseAll}
              onOpenForm={handlePosterClick}
            />
          </div>
        </div>
      )}

      {showSeminarModal && !isBlockedRoute && !isEventExpired() && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4"
          onClick={handleCloseAll}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <SeminarRegistration onClose={handleCloseAll} />
          </div>
        </div>
      )}
    </>
  );
}

class MyApp extends App {
  static async getInitialProps({ Component, ctx }) {
    let pageProps = {};

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx);
    }

    const authToken = ctx.req
      ? ctx.req.cookies.authToken
      : Cookies.get("authToken");

    return { pageProps, authToken };
  }

  render() {
    const { Component, pageProps, authToken } = this.props;

    return (
      <CartProvider>
        <div className="app-layout min-h-screen flex flex-col">
          <Header />

          <main className="flex-1">
            <Component {...pageProps} authToken={authToken} />
          </main>

          <CounsellingCTA />
          <Footer />

          <SeminarRegistrationModalManager />
        </div>
      </CartProvider>
    );
  }
}

export default MyApp;